import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  adminApi,
  adminAuth,
  macdLeads,
  formatApiErrorDetail,
} from "@/lib/adminApi";
import NoIndex from "@/components/admin/NoIndex";
import {
  Loader2,
  LogOut,
  MessageCircle,
  Search,
  Download,
  FileSpreadsheet,
  X,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Save,
} from "lucide-react";
import { toast } from "sonner";

const STATUSES = [
  "Novo",
  "Contatado",
  "Confirmado",
  "Participou",
  "Não participou",
  "Convertido",
  "Arquivado",
];

const STATUS_COLORS = {
  Novo: "bg-white/10 text-white/90 border-white/20",
  Contatado: "bg-blue-500/15 text-blue-200 border-blue-400/30",
  Confirmado: "bg-emerald-500/15 text-emerald-200 border-emerald-400/30",
  Participou: "bg-[#f4c542]/20 text-[#f4c542] border-[#f4c542]/40",
  "Não participou": "bg-rose-500/15 text-rose-200 border-rose-400/30",
  Convertido: "bg-purple-500/20 text-purple-200 border-purple-400/40",
  Arquivado: "bg-white/5 text-white/40 border-white/10",
};

const PERFIL_LABEL = {
  auxiliar: "Auxiliar",
  profissional: "Profissional",
  dono: "Dono / Gestor",
};
const TAMANHO_LABEL = { solo: "Solo", "2-5": "2–5", "6-15": "6–15", "16+": "16+" };
const TEMPO_LABEL = { "<1": "Menos de 1 ano", "1-3": "1–3 anos", "4-10": "4–10 anos", "10+": "10+ anos" };

const UF_LIST = [
  "AC","AL","AM","AP","BA","CE","DF","ES","GO","MA","MG","MS","MT","PA","PB",
  "PE","PI","PR","RJ","RN","RO","RR","RS","SC","SE","SP","TO",
];

// Editions of the MACD Connect. Retrocompatible with legacy source
// "macd-connect-2026" — those records remain untouched until the operator
// decides to migrate them. New editions get their own explicit identifier.
const EDITION_OPTIONS = [
  { value: "", label: "Todas as edições" },
  { value: "macd-connect-2026", label: "MACD Connect — legado (default original)" },
  { value: "macd-connect-online-ago-2026", label: "MACD Connect Online — Agosto 2026" },
  { value: "macd-connect-presencial-17ago2026", label: "MACD Connect Presencial — 31 Agosto 2026" },
  { value: "live-oasys-10ago2026", label: "LIVE 10/08 — Oasys Academy" },
];
const EDITION_LABEL = Object.fromEntries(
  EDITION_OPTIONS.filter((o) => o.value).map((o) => [o.value, o.label]),
);

const PAYMENT_OPTIONS = [
  { value: "", label: "Todos" },
  { value: "pending", label: "Aguardando pagamento" },
  { value: "paid", label: "Pago" },
  { value: "expired", label: "Expirado" },
  { value: "cancelled", label: "Cancelado" },
  { value: "refunded", label: "Estornado" },
  { value: "none", label: "N/A (edições legadas)" },
];

const PAYMENT_BADGE = {
  pending: "bg-amber-500/15 text-amber-200 border-amber-400/30",
  paid: "bg-emerald-500/15 text-emerald-200 border-emerald-400/30",
  expired: "bg-white/10 text-white/70 border-white/20",
  cancelled: "bg-rose-500/15 text-rose-200 border-rose-400/30",
  refunded: "bg-purple-500/15 text-purple-200 border-purple-400/30",
};
const PAYMENT_LABEL = {
  pending: "Aguardando",
  paid: "Pago",
  expired: "Expirado",
  cancelled: "Cancelado",
  refunded: "Estornado",
};

const PRESENCIAL_SOURCE = "macd-connect-presencial-17ago2026";

function formatBRL(cents) {
  if (typeof cents !== "number") return null;
  return (cents / 100).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
}

function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function normalizeWhats(w) {
  const digits = (w || "").replace(/\D/g, "");
  if (!digits) return "";
  if (digits.startsWith("55")) return digits;
  return `55${digits}`;
}

// ==============================================================
// UI subcomponents
// ==============================================================
const MetricCard = ({ label, value, testId, accent = false }) => (
  <div
    data-testid={testId}
    className={`p-6 border ${
      accent
        ? "bg-[#f4c542] text-[#0a0a0a] border-[#f4c542]"
        : "bg-[#141414] text-white border-white/10"
    }`}
  >
    <div
      className={`text-[10px] tracking-[0.24em] uppercase mb-3 ${
        accent ? "text-[#0a0a0a]/70" : "text-white/45"
      }`}
    >
      {label}
    </div>
    <div
      className="text-4xl md:text-[2.4rem] leading-none tracking-tight"
      style={{ fontFamily: "Inter Tight, sans-serif", fontWeight: 700 }}
    >
      {value}
    </div>
  </div>
);

const StatusBadge = ({ status }) => (
  <span
    className={`inline-flex items-center px-2.5 py-1 border text-[11px] uppercase tracking-[0.14em] ${
      STATUS_COLORS[status] || STATUS_COLORS.Novo
    }`}
  >
    {status}
  </span>
);

// ==============================================================
// Lead detail drawer (mobile-friendly full-screen)
// ==============================================================
function LeadDrawer({ lead, onClose, onSaved }) {
  const [status, setStatus] = useState(lead?.status || "Novo");
  const [notes, setNotes] = useState(lead?.notes || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setStatus(lead?.status || "Novo");
    setNotes(lead?.notes || "");
  }, [lead]);

  if (!lead) return null;

  const save = async () => {
    setSaving(true);
    try {
      const updated = await macdLeads.update(lead.id, { status, notes });
      toast.success("Alterações salvas.");
      onSaved(updated);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const waLink = `https://wa.me/${normalizeWhats(lead.whatsapp)}`;

  return (
    <div
      className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex justify-end"
      onClick={onClose}
      data-testid="lead-drawer"
    >
      <div
        className="w-full max-w-[560px] bg-[#0a0a0a] text-white h-full overflow-y-auto border-l border-white/10"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 md:px-8 py-6 flex items-start justify-between border-b border-white/10">
          <div className="min-w-0">
            <div className="text-[10px] tracking-[0.24em] uppercase text-white/45 mb-2">
              Inscrito · MACD Connect
            </div>
            <h2
              className="text-2xl md:text-[1.75rem] leading-tight tracking-tight truncate"
              style={{ fontFamily: "Inter Tight, sans-serif", fontWeight: 600 }}
              data-testid="drawer-lead-name"
            >
              {lead.nome}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-white/50 hover:text-white p-2 -mr-2"
            data-testid="drawer-close"
          >
            <X size={20} />
          </button>
        </div>

        <div className="px-6 md:px-8 py-6 space-y-6">
          <a
            href={waLink}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="drawer-whatsapp"
            className="flex items-center justify-center gap-2 bg-[#25D366] text-[#0a0a0a] font-medium py-3 uppercase text-[13px] tracking-[0.08em] hover:bg-[#22c05e] transition-colors"
          >
            <MessageCircle size={16} /> Abrir no WhatsApp
          </a>

          <div className="grid grid-cols-2 gap-4 text-sm">
            <Field label="WhatsApp" value={lead.whatsapp} />
            <Field label="E-mail" value={lead.email} />
            <Field label="Perfil" value={PERFIL_LABEL[lead.perfil] || lead.perfil} />
            <Field label="Salão" value={TAMANHO_LABEL[lead.tamanho_salao] || lead.tamanho_salao} />
            <Field label="Tempo" value={TEMPO_LABEL[lead.tempo_profissao] || lead.tempo_profissao} />
            <Field label="Cidade" value={lead.cidade} />
            <Field label="Estado" value={lead.estado} />
            <Field label="Origem" value={lead.source} />
            <Field
              label="Pagamento"
              value={
                lead.payment_status
                  ? `${PAYMENT_LABEL[lead.payment_status] || lead.payment_status}${
                      formatBRL(lead.payment_amount_cents)
                        ? ` · ${formatBRL(lead.payment_amount_cents)}`
                        : ""
                    }`
                  : "N/A"
              }
            />
            <Field label="Pago em" value={fmtDateTime(lead.payment_paid_at)} />
            <Field label="Inscrito em" value={fmtDateTime(lead.created_at)} />
            <Field label="Última atualização" value={fmtDateTime(lead.updated_at)} />
            <Field label="Contatado em" value={fmtDateTime(lead.contacted_at)} />
          </div>

          <div>
            <label className="block text-[11px] tracking-[0.24em] uppercase text-white/50 mb-2">
              Status
            </label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              data-testid="drawer-status-select"
              className="w-full bg-[#141414] border border-white/15 focus:border-[#f4c542] outline-none py-3 px-3 text-white text-[15px]"
            >
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-[11px] tracking-[0.24em] uppercase text-white/50 mb-2">
              Observações internas
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={6}
              data-testid="drawer-notes"
              placeholder="Notas de contato, contexto, próximos passos…"
              className="w-full bg-[#141414] border border-white/15 focus:border-[#f4c542] outline-none py-3 px-3 text-white text-[15px] resize-y"
            />
            <p className="mt-2 text-[11px] text-white/40">
              Não visíveis ao inscrito.
            </p>
          </div>

          <button
            onClick={save}
            disabled={saving}
            data-testid="drawer-save"
            className="w-full flex items-center justify-center gap-2 bg-[#f4c542] hover:bg-[#e8b930] text-[#0a0a0a] font-medium py-3.5 px-6 uppercase text-[13px] tracking-[0.08em] transition-colors disabled:opacity-60"
          >
            {saving ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />}
            Salvar alterações
          </button>
        </div>
      </div>
    </div>
  );
}

const Field = ({ label, value }) => (
  <div>
    <div className="text-[10px] tracking-[0.22em] uppercase text-white/40 mb-1">
      {label}
    </div>
    <div className="text-white/90 text-sm break-words">{value || "—"}</div>
  </div>
);

// ==============================================================
// Main page
// ==============================================================
export default function AdminMacdConnect() {
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [admin, setAdmin] = useState(null);

  const [metrics, setMetrics] = useState(null);
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(50);
  const [loading, setLoading] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);

  // filters
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [perfil, setPerfil] = useState("");
  const [cidade, setCidade] = useState("");
  const [estado, setEstado] = useState("");
  const [statusF, setStatusF] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [edicao, setEdicao] = useState("");
  const [paymentF, setPaymentF] = useState("");

  // Auth gate
  useEffect(() => {
    adminAuth
      .me()
      .then((u) => {
        setAdmin(u);
        setChecking(false);
      })
      .catch(() => {
        navigate("/admin/login", { replace: true });
      });
  }, [navigate]);

  const params = useMemo(
    () => ({
      search: search || undefined,
      perfil: perfil || undefined,
      cidade: cidade || undefined,
      estado: estado || undefined,
      status: statusF || undefined,
      date_from: dateFrom || undefined,
      date_to: dateTo || undefined,
      source: edicao || undefined,
      payment_status: paymentF || undefined,
      page,
      limit,
    }),
    [search, perfil, cidade, estado, statusF, dateFrom, dateTo, edicao, paymentF, page, limit],
  );

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      // Metrics are also scoped by the selected edition so panorama numbers
      // reflect exactly what the operator is looking at in the table.
      const metricsParams = edicao ? { source: edicao } : {};
      const [m, l] = await Promise.all([
        macdLeads.metrics(metricsParams),
        macdLeads.list(params),
      ]);
      setMetrics(m);
      setLeads(l.items);
      setTotal(l.total);
    } catch (err) {
      if (err.response?.status === 401) {
        navigate("/admin/login", { replace: true });
        return;
      }
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  }, [params, edicao, navigate]);

  useEffect(() => {
    if (admin) refresh();
  }, [admin, refresh]);

  // debounce search input
  useEffect(() => {
    const t = setTimeout(() => {
      setPage(1);
      setSearch(searchInput);
    }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const clearFilters = () => {
    setSearchInput("");
    setSearch("");
    setPerfil("");
    setCidade("");
    setEstado("");
    setStatusF("");
    setDateFrom("");
    setDateTo("");
    setEdicao("");
    setPaymentF("");
    setPage(1);
  };

  const anyFilter =
    search || perfil || cidade || estado || statusF || dateFrom || dateTo || edicao || paymentF;

  const doExport = async (kind) => {
    try {
      const { page: _p, limit: _l, ...exp } = params;
      await macdLeads.downloadExport(kind, exp);
      toast.success(`Exportação ${kind.toUpperCase()} gerada.`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const doLogout = async () => {
    await adminAuth.logout();
    navigate("/admin/login", { replace: true });
  };

  const changeStatusInline = async (lead, newStatus) => {
    try {
      const updated = await macdLeads.update(lead.id, { status: newStatus });
      setLeads((prev) => prev.map((l) => (l.id === lead.id ? updated : l)));
      toast.success("Status atualizado.");
      // Refresh metrics to keep counts consistent
      macdLeads.metrics().then(setMetrics).catch(() => {});
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center text-white/60">
        <NoIndex />
        <Loader2 className="animate-spin" size={20} />
      </div>
    );
  }

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white" data-testid="admin-dashboard">
      <NoIndex />

      {/* Header */}
      <header className="border-b border-white/10 sticky top-0 bg-[#0a0a0a]/95 backdrop-blur z-30">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-5 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4 min-w-0">
            <span className="inline-block w-6 h-[3px] bg-[#f4c542] shrink-0" />
            <div className="min-w-0">
              <div className="text-[10px] tracking-[0.24em] uppercase text-white/50">
                Oasys Academy · CRM
              </div>
              <h1
                className="text-lg md:text-xl tracking-tight truncate"
                style={{ fontFamily: "Inter Tight, sans-serif", fontWeight: 600 }}
              >
                MACD Connect · Inscritos
              </h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={refresh}
              title="Atualizar"
              data-testid="admin-refresh"
              className="text-white/60 hover:text-white p-2 border border-white/10 hover:border-white/30"
            >
              <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
            </button>
            <button
              onClick={doLogout}
              data-testid="admin-logout"
              className="flex items-center gap-2 text-white/70 hover:text-white text-xs uppercase tracking-[0.14em] px-3 py-2 border border-white/10 hover:border-white/30"
            >
              <LogOut size={14} /> Sair
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 md:px-10 py-10 md:py-14 space-y-10">
        {/* Metrics */}
        <section>
          <div className="text-[11px] tracking-[0.24em] uppercase text-white/45 mb-4">
            Panorama
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <MetricCard
              label="Total de inscritos"
              value={metrics?.total ?? "—"}
              testId="metric-total"
              accent
            />
            <MetricCard label="Hoje" value={metrics?.today ?? "—"} testId="metric-today" />
            <MetricCard label="Últimos 7 dias" value={metrics?.last_7d ?? "—"} testId="metric-7d" />
            <MetricCard
              label="Última inscrição"
              value={
                <span className="text-base md:text-lg font-medium">
                  {fmtDateTime(metrics?.last_created_at)}
                </span>
              }
              testId="metric-last"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
            <BreakdownList
              title="Por perfil"
              items={metrics?.by_perfil}
              labelMap={PERFIL_LABEL}
              testId="breakdown-perfil"
            />
            <BreakdownList
              title="Por estado"
              items={metrics?.by_estado}
              testId="breakdown-estado"
            />
          </div>

          {edicao === PRESENCIAL_SOURCE && metrics?.by_payment && (
            <div
              data-testid="presencial-payment-summary"
              className="mt-3 border border-[#f4c542]/30 bg-[#f4c542]/[0.06] p-5 md:p-6"
            >
              <div className="text-[11px] tracking-[0.24em] uppercase text-[#f4c542]/90 mb-4">
                MACD Connect Presencial · Pagamentos
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <PaymentStat
                  label="Inscritos"
                  value={metrics.total ?? 0}
                  testId="pay-stat-total"
                />
                <PaymentStat
                  label="Pagos"
                  value={metrics.by_payment.paid || 0}
                  tone="paid"
                  testId="pay-stat-paid"
                />
                <PaymentStat
                  label="Aguardando"
                  value={metrics.by_payment.pending || 0}
                  tone="pending"
                  testId="pay-stat-pending"
                />
                <PaymentStat
                  label="Expirados / Cancelados"
                  value={(metrics.by_payment.expired || 0) + (metrics.by_payment.cancelled || 0)}
                  tone="muted"
                  testId="pay-stat-expired"
                />
              </div>
            </div>
          )}
        </section>

        {/* Filters */}
        <section className="border border-white/10 bg-[#141414] p-5 md:p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="text-[11px] tracking-[0.24em] uppercase text-white/45">
              Filtros
            </div>
            {anyFilter && (
              <button
                onClick={clearFilters}
                data-testid="clear-filters"
                className="text-white/60 hover:text-white text-xs uppercase tracking-[0.14em] flex items-center gap-1"
              >
                <X size={12} /> Limpar
              </button>
            )}
          </div>

          <div className="mb-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-[10px] tracking-[0.24em] uppercase text-white/50 mb-2">
                Edição
              </label>
              <select
                value={edicao}
                onChange={(e) => {
                  setPage(1);
                  setEdicao(e.target.value);
                }}
                data-testid="filter-edicao"
                className="w-full bg-[#0a0a0a] border border-white/15 focus:border-[#f4c542] outline-none py-2.5 px-3 text-sm"
              >
                {EDITION_OPTIONS.map((o) => (
                  <option key={o.value || "all"} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-[10px] tracking-[0.24em] uppercase text-white/50 mb-2">
                Pagamento
              </label>
              <select
                value={paymentF}
                onChange={(e) => {
                  setPage(1);
                  setPaymentF(e.target.value);
                }}
                data-testid="filter-payment"
                className="w-full bg-[#0a0a0a] border border-white/15 focus:border-[#f4c542] outline-none py-2.5 px-3 text-sm"
              >
                {PAYMENT_OPTIONS.map((o) => (
                  <option key={o.value || "all"} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="relative md:col-span-2">
              <Search
                size={14}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40"
              />
              <input
                type="text"
                placeholder="Buscar por nome ou WhatsApp"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                data-testid="filter-search"
                className="w-full bg-[#0a0a0a] border border-white/15 focus:border-[#f4c542] outline-none py-2.5 pl-9 pr-3 text-sm"
              />
            </div>

            <select
              value={perfil}
              onChange={(e) => {
                setPage(1);
                setPerfil(e.target.value);
              }}
              data-testid="filter-perfil"
              className="bg-[#0a0a0a] border border-white/15 focus:border-[#f4c542] outline-none py-2.5 px-3 text-sm"
            >
              <option value="">Perfil — todos</option>
              {Object.entries(PERFIL_LABEL).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </select>

            <select
              value={statusF}
              onChange={(e) => {
                setPage(1);
                setStatusF(e.target.value);
              }}
              data-testid="filter-status"
              className="bg-[#0a0a0a] border border-white/15 focus:border-[#f4c542] outline-none py-2.5 px-3 text-sm"
            >
              <option value="">Status — todos</option>
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>

            <input
              type="text"
              placeholder="Cidade"
              value={cidade}
              onChange={(e) => {
                setPage(1);
                setCidade(e.target.value);
              }}
              data-testid="filter-cidade"
              className="bg-[#0a0a0a] border border-white/15 focus:border-[#f4c542] outline-none py-2.5 px-3 text-sm"
            />

            <select
              value={estado}
              onChange={(e) => {
                setPage(1);
                setEstado(e.target.value);
              }}
              data-testid="filter-estado"
              className="bg-[#0a0a0a] border border-white/15 focus:border-[#f4c542] outline-none py-2.5 px-3 text-sm"
            >
              <option value="">Estado — todos</option>
              {UF_LIST.map((uf) => (
                <option key={uf} value={uf}>
                  {uf}
                </option>
              ))}
            </select>

            <input
              type="date"
              value={dateFrom}
              onChange={(e) => {
                setPage(1);
                setDateFrom(e.target.value);
              }}
              data-testid="filter-date-from"
              className="bg-[#0a0a0a] border border-white/15 focus:border-[#f4c542] outline-none py-2.5 px-3 text-sm text-white/80"
            />
            <input
              type="date"
              value={dateTo}
              onChange={(e) => {
                setPage(1);
                setDateTo(e.target.value);
              }}
              data-testid="filter-date-to"
              className="bg-[#0a0a0a] border border-white/15 focus:border-[#f4c542] outline-none py-2.5 px-3 text-sm text-white/80"
            />
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 mt-5 pt-5 border-t border-white/10">
            <div className="text-xs text-white/50">
              <span className="text-white/90 font-medium" data-testid="results-count">
                {total.toLocaleString("pt-BR")}
              </span>{" "}
              {total === 1 ? "resultado" : "resultados"}
              {anyFilter && " (filtrado)"}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => doExport("csv")}
                data-testid="export-csv"
                className="flex items-center gap-2 text-xs uppercase tracking-[0.14em] px-3 py-2 border border-white/15 hover:border-[#f4c542] hover:text-[#f4c542]"
              >
                <Download size={13} /> CSV
              </button>
              <button
                onClick={() => doExport("xlsx")}
                data-testid="export-xlsx"
                className="flex items-center gap-2 text-xs uppercase tracking-[0.14em] px-3 py-2 border border-white/15 hover:border-[#f4c542] hover:text-[#f4c542]"
              >
                <FileSpreadsheet size={13} /> Excel
              </button>
            </div>
          </div>
        </section>

        {/* Table */}
        <section>
          {/* Desktop table */}
          <div className="hidden md:block border border-white/10 overflow-x-auto">
            <table className="w-full text-sm" data-testid="leads-table">
              <thead className="bg-[#141414] text-white/50 text-[10px] uppercase tracking-[0.16em]">
                <tr>
                  <th className="text-left px-4 py-3">Nome</th>
                  <th className="text-left px-4 py-3">WhatsApp</th>
                  <th className="text-left px-4 py-3">Perfil</th>
                  <th className="text-left px-4 py-3">Cidade / UF</th>
                  <th className="text-left px-4 py-3">Inscrito em</th>
                  <th className="text-left px-4 py-3">Pagamento</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-right px-4 py-3">Ações</th>
                </tr>
              </thead>
              <tbody>
                {loading && leads.length === 0 && (
                  <tr>
                    <td colSpan={8} className="text-center py-16 text-white/40">
                      <Loader2 className="animate-spin inline mr-2" size={16} />
                      Carregando…
                    </td>
                  </tr>
                )}
                {!loading && leads.length === 0 && (
                  <tr>
                    <td colSpan={8} className="text-center py-16 text-white/40">
                      Nenhum inscrito encontrado.
                    </td>
                  </tr>
                )}
                {leads.map((l) => (
                  <tr
                    key={l.id}
                    className="border-t border-white/5 hover:bg-white/[0.02]"
                    data-testid={`lead-row-${l.id}`}
                  >
                    <td className="px-4 py-3">
                      <button
                        onClick={() => setSelectedLead(l)}
                        className="text-white hover:text-[#f4c542] text-left"
                      >
                        {l.nome}
                      </button>
                      {l.notes && (
                        <div className="text-[11px] text-white/40 mt-1 line-clamp-1 max-w-xs">
                          {l.notes}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-white/70 tabular-nums">
                      {l.whatsapp}
                    </td>
                    <td className="px-4 py-3 text-white/70">
                      {PERFIL_LABEL[l.perfil] || l.perfil}
                    </td>
                    <td className="px-4 py-3 text-white/70">
                      {l.cidade} · {l.estado}
                    </td>
                    <td className="px-4 py-3 text-white/60 tabular-nums">
                      {fmtDateTime(l.created_at)}
                    </td>
                    <td className="px-4 py-3">
                      {l.payment_status ? (
                        <span
                          data-testid={`row-payment-${l.id}`}
                          className={`inline-flex items-center px-2 py-0.5 border text-[10px] uppercase tracking-[0.14em] ${
                            PAYMENT_BADGE[l.payment_status] || "bg-white/5 text-white/50 border-white/10"
                          }`}
                          title={
                            l.payment_paid_at
                              ? `Pago em ${fmtDateTime(l.payment_paid_at)}`
                              : formatBRL(l.payment_amount_cents) || ""
                          }
                        >
                          {PAYMENT_LABEL[l.payment_status] || l.payment_status}
                        </span>
                      ) : (
                        <span
                          data-testid={`row-payment-${l.id}`}
                          className="text-[10px] uppercase tracking-[0.14em] text-white/30"
                        >
                          N/A
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <select
                        value={l.status}
                        onChange={(e) => changeStatusInline(l, e.target.value)}
                        data-testid={`row-status-${l.id}`}
                        className={`bg-transparent border text-[11px] uppercase tracking-[0.14em] py-1 pl-2 pr-1 outline-none cursor-pointer ${
                          STATUS_COLORS[l.status] || STATUS_COLORS.Novo
                        }`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {STATUSES.map((s) => (
                          <option key={s} value={s} className="bg-[#0a0a0a]">
                            {s}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <a
                          href={`https://wa.me/${normalizeWhats(l.whatsapp)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          title="Abrir no WhatsApp"
                          data-testid={`wa-${l.id}`}
                          className="p-2 text-white/60 hover:text-[#25D366] hover:bg-white/5"
                        >
                          <MessageCircle size={16} />
                        </a>
                        <button
                          onClick={() => setSelectedLead(l)}
                          data-testid={`open-${l.id}`}
                          className="text-[11px] uppercase tracking-[0.14em] px-3 py-1.5 border border-white/15 hover:border-[#f4c542] hover:text-[#f4c542]"
                        >
                          Abrir
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden space-y-2">
            {loading && leads.length === 0 && (
              <div className="text-center py-16 text-white/40">
                <Loader2 className="animate-spin inline mr-2" size={16} />
                Carregando…
              </div>
            )}
            {!loading && leads.length === 0 && (
              <div className="text-center py-16 text-white/40 border border-white/10">
                Nenhum inscrito encontrado.
              </div>
            )}
            {leads.map((l) => (
              <button
                key={l.id}
                onClick={() => setSelectedLead(l)}
                data-testid={`lead-card-${l.id}`}
                className="w-full text-left border border-white/10 bg-[#141414] p-4 hover:border-white/25"
              >
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="min-w-0">
                    <div className="text-white font-medium truncate">{l.nome}</div>
                    <div className="text-xs text-white/50 mt-0.5">
                      {l.cidade} · {l.estado}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <StatusBadge status={l.status} />
                    {l.payment_status && (
                      <span
                        className={`inline-flex items-center px-2 py-0.5 border text-[9px] uppercase tracking-[0.14em] ${
                          PAYMENT_BADGE[l.payment_status] || "bg-white/5 text-white/50 border-white/10"
                        }`}
                      >
                        {PAYMENT_LABEL[l.payment_status] || l.payment_status}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center justify-between text-xs text-white/50 mt-3">
                  <span>{PERFIL_LABEL[l.perfil] || l.perfil}</span>
                  <span className="tabular-nums">{fmtDateTime(l.created_at)}</span>
                </div>
              </button>
            ))}
          </div>

          {/* Pagination */}
          {total > limit && (
            <div className="flex items-center justify-between mt-6 text-sm text-white/60">
              <button
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                data-testid="page-prev"
                className="flex items-center gap-1 disabled:opacity-30 hover:text-white"
              >
                <ChevronLeft size={16} /> Anterior
              </button>
              <span>
                Página <span className="text-white">{page}</span> de {totalPages}
              </span>
              <button
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                data-testid="page-next"
                className="flex items-center gap-1 disabled:opacity-30 hover:text-white"
              >
                Próxima <ChevronRight size={16} />
              </button>
            </div>
          )}
        </section>
      </main>

      <LeadDrawer
        lead={selectedLead}
        onClose={() => setSelectedLead(null)}
        onSaved={(updated) => {
          setLeads((prev) => prev.map((l) => (l.id === updated.id ? updated : l)));
          setSelectedLead(updated);
          macdLeads.metrics().then(setMetrics).catch(() => {});
        }}
      />
    </div>
  );
}

function PaymentStat({ label, value, tone = "default", testId }) {
  const toneClass = {
    paid: "text-emerald-300",
    pending: "text-amber-300",
    muted: "text-white/50",
    default: "text-white",
  }[tone];
  return (
    <div
      data-testid={testId}
      className="bg-[#0a0a0a]/70 border border-white/10 p-4"
    >
      <div className="text-[10px] tracking-[0.24em] uppercase text-white/45 mb-2">
        {label}
      </div>
      <div
        className={`text-3xl tracking-tight ${toneClass}`}
        style={{ fontFamily: "Inter Tight, sans-serif", fontWeight: 700 }}
      >
        {value.toLocaleString("pt-BR")}
      </div>
    </div>
  );
}

function BreakdownList({ title, items, labelMap, testId }) {
  const data = items || [];
  return (
    <div
      className="border border-white/10 bg-[#141414] p-5"
      data-testid={testId}
    >
      <div className="text-[10px] tracking-[0.24em] uppercase text-white/45 mb-3">
        {title}
      </div>
      {data.length === 0 ? (
        <div className="text-sm text-white/40">Sem dados.</div>
      ) : (
        <ul className="space-y-2">
          {data.slice(0, 8).map((it) => (
            <li
              key={it.key}
              className="flex items-center justify-between text-sm"
            >
              <span className="text-white/80">
                {labelMap?.[it.key] || it.key}
              </span>
              <span className="tabular-nums text-white/60">
                {it.count.toLocaleString("pt-BR")}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
